import React, { useState, useEffect, useContext } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SnackbarContext } from '../App';
import api from '../api/api';
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from "./ui/sheet";
import SequenceEnrollModal from './SequenceEnrollModal';
import AddToCampaignModal from './AddToCampaignModal';
import { Badge } from "./ui/badge";
import {
    ChevronRight, CheckCircle2,
    Clock, BarChart3, Layers, Mail,
    RefreshCw, X, Tag, Download, Plus, FileText, Pencil, Building, Globe, Database
} from 'lucide-react';
import ScoreBadge from './ui/ScoreBadge';
import useListContext from '../hooks/useListContext';
import SavedViewBar from './SavedViewBar';
import { useConfirm } from '../contexts/ConfirmContext';
import ContactCreateModal from './ContactCreateModal';
import CompanyDetailDrawer from './CompanyDetailDrawer';
import PageHeader from './ui/PageHeader';
import StatCard from './ui/StatCard';
import DataTable from './ui/DataTable';
import StatusBadge from './ui/StatusBadge';
import ContactDetailDrawer from './ContactDetailDrawer';
import { getStageBadge, getVerificationIcon } from '../utils/crmHelpers';
import ContactImportIntakeModal from './ContactImportIntakeModal';
import { formatRelativeDistance, normalizeApiError } from '../lib/i18n';

const Contacts = ({ onSwitchTab }) => {
    const showSnackbar = useContext(SnackbarContext);
    const { t, i18n } = useTranslation();
    const [contacts, setContacts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [stageFilter, setStageFilter] = useState('ALL');
    const [verificationFilter, setVerificationFilter] = useState('ALL');
    const [contactedFilter, setContactedFilter] = useState('ALL');
    const [staleFilter, setStaleFilter] = useState('ALL');
    const [hasOverrideFilter, setHasOverrideFilter] = useState('ALL');
    const [tagsFilter, setTagsFilter] = useState('');
    const [selectedContacts, setSelectedContacts] = useState([]);

    // Pagination & Sorting
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const [sortBy, setSortBy] = useState('createdAt');
    const [sortOrder, setSortOrder] = useState('desc');

    // Campaign Binding State
    const [isEnrollModalOpen, setIsEnrollModalOpen] = useState(false);
    const [isAddCampaignOpen, setIsAddCampaignOpen] = useState(false);

    const handleOpenCampaignModal = () => {
        if (selectedContacts.length === 0) {
            showSnackbar(t('contacts.selectAtLeastOne'), 'info');
            return;
        }
        setIsAddCampaignOpen(true);
    };

    // Contact Detail State
    const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
    const [selectedContactIdForDetail, setSelectedContactIdForDetail] = useState(null);

    // CRM Edit State

    // Bulk Action State
    const [isBulkStageOpen, setIsBulkStageOpen] = useState(false);
    const [bulkStageValue, setBulkStageValue] = useState('LEAD');
    const [isBulkTagOpen, setIsBulkTagOpen] = useState(false);
    const [bulkTagValue, setBulkTagValue] = useState('');
    const [bulkTagMode, setBulkTagMode] = useState('add');
    const [isBulkProcessing, setIsBulkProcessing] = useState(false);

    // Funnel State
    const [funnel, setFunnel] = useState(null);

    // Approvals State
    const [crmApprovals, setCrmApprovals] = useState([]);
    const [isExporting, setIsExporting] = useState(false);

    // Tag Management State
    const [isTagManagerOpen, setIsTagManagerOpen] = useState(false);
    const [workspaceTags, setWorkspaceTags] = useState([]);

    // New CRM State
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [isCompanyDrawerOpen, setIsCompanyDrawerOpen] = useState(false);
    const [selectedCompanyId, setSelectedCompanyId] = useState(null);
    const [companyFilter, setCompanyFilter] = useState('ALL');
    const [workspaceCompanies, setWorkspaceCompanies] = useState([]);
    const [isImportModalOpen, setIsImportModalOpen] = useState(false);

    // Saved View integration via centralized hook
    const listContext = useListContext({
        moduleType: 'CONTACTS',
        defaultFilters: { stageFilter: 'ALL', verificationFilter: 'ALL', contactedFilter: 'ALL', staleFilter: 'ALL', hasOverrideFilter: 'ALL', tagsFilter: '', companyFilter: 'ALL' },
        defaultSort: { field: 'createdAt', order: 'desc' }
    });

    const handleApplyView = (view) => {
        listContext.applyView(view);
        const fs = view.filterState instanceof Map ? Object.fromEntries(view.filterState) : (view.filterState || {});
        if (fs.stageFilter) setStageFilter(fs.stageFilter);
        if (fs.verificationFilter) setVerificationFilter(fs.verificationFilter);
        if (fs.contactedFilter) setContactedFilter(fs.contactedFilter);
        if (fs.staleFilter) setStaleFilter(fs.staleFilter);
        if (fs.hasOverrideFilter) setHasOverrideFilter(fs.hasOverrideFilter);
        if (fs.tagsFilter !== undefined) setTagsFilter(fs.tagsFilter);
        if (fs.companyFilter !== undefined) setCompanyFilter(fs.companyFilter);
        const ss = view.sortState instanceof Map ? Object.fromEntries(view.sortState) : (view.sortState || {});
        if (ss.field) setSortBy(ss.field);
        if (ss.order) setSortOrder(ss.order);
        setPage(1);
    };

    const handleSaveView = async (name) => {
        return listContext.saveCurrentView(name);
    };

    const handleClearAllFilters = () => {
        listContext.clearFilters();
        setStageFilter('ALL');
        setVerificationFilter('ALL');
        setContactedFilter('ALL');
        setStaleFilter('ALL');
        setHasOverrideFilter('ALL');
        setTagsFilter('');
        setCompanyFilter('ALL');
        setSortBy('createdAt');
        setSortOrder('desc');
        setPage(1);
    };

    // Keep listContext filters in sync whenever local filters change
    useEffect(() => {
        listContext.setFilters({ stageFilter, verificationFilter, contactedFilter, staleFilter, hasOverrideFilter, tagsFilter, companyFilter });
    }, [stageFilter, verificationFilter, contactedFilter, staleFilter, hasOverrideFilter, tagsFilter, companyFilter]);

    useEffect(() => {
        listContext.setSort({ field: sortBy, order: sortOrder });
    }, [sortBy, sortOrder]);

    const hasActiveFilters = stageFilter !== 'ALL' || verificationFilter !== 'ALL' || contactedFilter !== 'ALL' || staleFilter !== 'ALL' || hasOverrideFilter !== 'ALL' || tagsFilter !== '' || companyFilter !== 'ALL';

    const fetchContacts = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({
                page,
                limit: 25,
                sortBy,
                sortOrder
            });

            if (search) params.append('search', search);
            if (stageFilter && stageFilter !== 'ALL') params.append('stage', stageFilter);
            if (verificationFilter && verificationFilter !== 'ALL') params.append('verificationStatus', verificationFilter);
            if (contactedFilter !== 'ALL') params.append('contacted', contactedFilter);
            if (staleFilter !== 'ALL') params.append('stale', staleFilter);
            if (hasOverrideFilter !== 'ALL') params.append('hasOverride', hasOverrideFilter);
            if (tagsFilter) params.append('tags', tagsFilter);
            if (companyFilter !== 'ALL') params.append('companyId', companyFilter);

            const response = await api.get(`/api/contacts?${params.toString()}`);

            setContacts(response.data.data);
            setTotalPages(response.data.pagination.totalPages);
            setTotalRecords(response.data.pagination.total);
        } catch (error) {
            console.error('Error fetching contacts:', error);
            showSnackbar(normalizeApiError(error, t, 'contacts.loadError'), 'error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchContacts();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, stageFilter, verificationFilter, contactedFilter, staleFilter, hasOverrideFilter, tagsFilter, companyFilter, sortBy, sortOrder]);

    // Deep-link: ?focus=<id> auto-opens contact detail
    const [searchParams, setSearchParams] = useSearchParams();
    useEffect(() => {
        const focusId = searchParams.get('focus');
        if (focusId && !loading) {
            handleOpenDetail(focusId);
            setSearchParams({}, { replace: true });
        }
    }, [searchParams, loading, setSearchParams]);

    const fetchWorkspaceTags = async () => {
        try {
            const res = await api.get('/api/contacts/tags');
            setWorkspaceTags(res.data);
        } catch (e) { /* silent */ }
    };

    // Fetch funnel summary
    const fetchFunnel = async () => {
        try {
            const res = await api.get('/api/contacts/funnel');
            setFunnel(res.data);
        } catch (e) { /* silent */ }
    };

    const fetchCompaniesDropdown = async () => {
        try {
            const res = await api.get('/api/crm/companies?limit=250');
            setWorkspaceCompanies(res.data.data || []);
        } catch (e) { /* silent */ }
    };

    useEffect(() => {
        let mounted = true;
        const init = async () => {
            await Promise.all([
                fetchFunnel(),
                fetchApprovals(),
                fetchWorkspaceTags(),
                fetchCompaniesDropdown()
            ]);
            if (mounted) {
                // Initial data load complete
            }
        };
        init();
        return () => { mounted = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchApprovals = async () => {
        try {
            const res = await api.get('/api/contacts/approvals');
            setCrmApprovals(res.data);
        } catch (e) { /* silent */ }
    };

    const handleApprovalAction = async (id, action) => {
        try {
            let endpoint = '';
            let method = 'post';
            let payload = {};

            if (action === 'approve') {
                endpoint = `/approvals/${id}/approve`;
                method = 'put';
            } else if (action === 'reject') {
                endpoint = `/approvals/${id}/reject`;
                method = 'put';
                payload = { reason: 'UI üzerinden reddedildi' };
            } else if (action === 'execute') {
                endpoint = `/approvals/${id}/execute`;
            }

            await api[method](endpoint, payload);

            showSnackbar(`Talep başarıyla ${action === 'approve' ? 'onaylandı' : action === 'reject' ? 'reddedildi' : 'çalıştırıldı'}`, 'success');
            fetchApprovals();
            if (action === 'execute') {
                fetchContacts();
                fetchFunnel();
            }
        } catch (error) {
            showSnackbar(error.isAppError ? error.message : 'İşlem başarısız', 'error');
        }
    };

    const handleBulkStageUpdate = async () => {
        if (selectedContacts.length === 0) return;
        setIsBulkProcessing(true);
        try {
            const res = await api.post('/api/contacts/bulk-update',
                { contactIds: selectedContacts, updates: { stage: bulkStageValue } }
            );
            if (res.status === 202 || res.data.approvalRequired) {
                showSnackbar(`${selectedContacts.length} kişi için onay talebi oluşturuldu. Yönetici onayı bekleniyor.`, 'warning');
            } else {
                showSnackbar(`${res.data.modified} kişinin aşaması güncellendi`, 'success');
            }
            setIsBulkStageOpen(false);
            setSelectedContacts([]);
            fetchContacts();
            fetchFunnel();
        } catch (error) {
            showSnackbar(error.isAppError ? error.message : 'Toplu güncelleme başarısız', 'error');
        } finally {
            setIsBulkProcessing(false);
        }
    };

    const handleBulkTagAdd = async () => {
        if (selectedContacts.length === 0 || !bulkTagValue.trim()) return;
        setIsBulkProcessing(true);
        try {
            const newTags = bulkTagValue.split(',').map(t => t.trim()).filter(Boolean);
            const res = await api.post('/api/contacts/bulk-tags',
                { contactIds: selectedContacts, mode: bulkTagMode, tags: newTags }
            );
            const modeLabel = bulkTagMode === 'add' ? 'eklendi' : bulkTagMode === 'remove' ? 'kaldırıldı' : 'değiştirildi';
            showSnackbar(`${res.data.modified} kişide etiket ${modeLabel}`, 'success');
            setIsBulkTagOpen(false);
            setBulkTagValue('');
            setSelectedContacts([]);
            fetchContacts();
        } catch (error) {
            showSnackbar(error.isAppError ? error.message : 'Etiket işlemi başarısız', 'error');
        } finally {
            setIsBulkProcessing(false);
        }
    };

    const handleRenameTag = async (oldTag) => {
        const newTag = prompt(`'${oldTag}' etiketinin yeni adını girin:`);
        if (!newTag || !newTag.trim() || newTag.trim() === oldTag) return;
        try {
            await api.put(`/api/contacts/tags/${oldTag}/rename`,
                { newTag: newTag.trim() }
            );
            showSnackbar(`Etiket '${newTag}' olarak başarıyla yenilendi`, 'success');
            fetchWorkspaceTags();
            fetchContacts();
        } catch (error) {
            showSnackbar(error.isAppError ? error.message : 'Etiket yenileme başarısız', 'error');
        }
    };

    const confirm = useConfirm();

    const handleDeleteTag = async (tag) => {
        const ok = await confirm("Delete Tag", `'${tag}' etiketini tüm kişilerden tamamen silmek istediğinize emin misiniz?`);
        if (!ok) return;
        try {
            await api.delete(`/api/contacts/tags/${tag}`);
            showSnackbar(`Etiket başarıyla silindi`, 'success');
            fetchWorkspaceTags();
            fetchContacts();
        } catch (error) {
            showSnackbar(error.isAppError ? error.message : 'Etiket silme başarısız', 'error');
        }
    };

    const handleExport = async (mode) => {
        setIsExporting(true);
        try {
            const payload = {};
            if (mode === 'selected' && selectedContacts.length > 0) {
                payload.contactIds = selectedContacts;
            } else {
                if (stageFilter && stageFilter !== 'ALL') payload.stage = stageFilter;
                if (verificationFilter && verificationFilter !== 'ALL') payload.verificationStatus = verificationFilter;
                if (search) payload.search = search;
            }

            const res = await api.post('/api/contacts/export', payload, {
                responseType: 'blob',
                validateStatus: (s) => s < 500
            });

            // Handle 202 approval-required response
            if (res.status === 202) {
                const text = await res.data.text();
                const parsed = JSON.parse(text);
                showSnackbar(parsed.message || 'Export için onay gerekiyor', 'warning');
                return;
            }

            const blob = new Blob([res.data], { type: 'text/csv' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'crm_contacts_export.csv';
            a.click();
            window.URL.revokeObjectURL(url);
            showSnackbar('CSV indirildi', 'success');
        } catch (error) {
            showSnackbar('Export başarısız oldu', 'error');
        } finally {
            setIsExporting(false);
        }
    };

    const handleOpenDetail = (id) => {
        setSelectedContactIdForDetail(id);
        setIsDetailModalOpen(true);
    };





    return (
        <div className="space-y-8 animate-in fade-in duration-500">
            <PageHeader
                title={t('contacts.title')}
                subtitle={t('contacts.subtitle')}
                icon={Database}
                actions={
                    <>
                        <Button
                            variant="outline"
                            onClick={() => setIsImportModalOpen(true)}
                            className="rounded-xl font-black text-[11px] uppercase tracking-widest"
                        >
                            <FileText className="w-4 h-4 mr-2" /> {t('csvUpload.title')}
                        </Button>
                        <Button
                            onClick={() => setIsCreateModalOpen(true)}
                            className="rounded-xl font-black text-[11px] uppercase tracking-widest"
                        >
                            <Plus className="w-4 h-4 mr-2" /> {t('dashboard.shortcuts.addContacts')}
                        </Button>
                    </>
                }
            />

            {/* Page Header Actions - Shown only when items are selected */}
            {selectedContacts.length > 0 && (
                <div className="sticky top-0 z-10 flex flex-wrap items-center gap-3 bg-primary/5 backdrop-blur-md p-3 rounded-2xl border border-primary/20 shadow-xl lg:px-6 mb-4 animate-in slide-in-from-top-4 duration-300">
                    <span className="px-4 py-1 bg-primary text-white text-[10px] font-black uppercase tracking-widest rounded-full shadow-sm">
                        {t('common.selectedCount', { count: selectedContacts.length })}
                    </span>
                    <div className="h-6 w-px bg-primary/10 mx-2 hidden sm:block" />
                    <Button onClick={handleOpenCampaignModal} className="h-9 px-4 text-xs font-black uppercase tracking-widest bg-indigo-600 hover:bg-indigo-700 shadow-lg border-0 gap-2 rounded-xl transition-all active:scale-95">
                        <Plus className="w-4 h-4" /> {t('campaigns.create')}
                    </Button>
                    <Button onClick={() => setIsBulkStageOpen(true)} variant="outline" className="h-9 px-4 text-xs font-black uppercase tracking-widest border-primary/20 hover:bg-primary/5 gap-2 rounded-xl transition-all">
                        <Layers className="w-4 h-4" /> Change Stage
                    </Button>
                    <Button onClick={() => setIsBulkTagOpen(true)} variant="outline" className="h-9 px-4 text-xs font-black uppercase tracking-widest border-primary/20 hover:bg-primary/5 gap-2 rounded-xl transition-all">
                        <Tag className="w-4 h-4" /> Add Tag
                    </Button>
                    <Button onClick={() => handleExport('selected')} variant="outline" disabled={isExporting} className="h-9 px-4 text-xs font-black uppercase tracking-widest border-primary/20 hover:bg-primary/5 gap-2 rounded-xl transition-all">
                        <Download className="w-4 h-4" /> {isExporting ? t('common.loading') : t('actions.export')}
                    </Button>
                    <Button onClick={() => setSelectedContacts([])} variant="ghost" className="h-9 w-9 p-0 rounded-xl hover:bg-destructive/10 hover:text-destructive group ml-auto transition-all">
                        <X className="w-4 h-4 group-hover:rotate-90 transition-transform" />
                    </Button>
                </div>
            )}

            {/* CRM Funnel Intelligence */}
            {funnel && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                    <StatCard
                        title="Total Base"
                        value={funnel.total}
                        icon={BarChart3}
                        description={`${funnel.contacted} contacted · ${funnel.uncontacted} pending`}
                        trend={`${funnel.total ? Math.round((funnel.contacted / funnel.total) * 100) : 0}% Contacted`}
                    />
                    <StatCard
                        title="Verifications"
                        value={funnel.verificationDistribution?.VERIFIED || 0}
                        icon={CheckCircle2}
                        description={`${funnel.verificationDistribution?.RISKY || 0} risky · ${funnel.verificationDistribution?.INVALID || 0} invalid`}
                        variant="success"
                    />
                    <StatCard
                        title="Sales Pipeline"
                        value={funnel.stageDistribution?.CONTACTED || 0}
                        icon={Layers}
                        description={`${funnel.stageDistribution?.REPLIED || 0} replies · ${funnel.stageDistribution?.MEETING_BOOKED || 0} meetings`}
                        variant="warning"
                    />
                    <StatCard
                        title="Data Health"
                        value={funnel.fresh}
                        icon={RefreshCw}
                        description={`${funnel.stale} stale records`}
                        trend={`${funnel.total ? Math.round((funnel.fresh / funnel.total) * 100) : 0}% Fresh`}
                        variant={funnel.stale > funnel.fresh ? 'danger' : 'default'}
                    />
                </div>
            )}

            {/* CRM Pending Actions (Approvals) */}
            {crmApprovals.length > 0 && (
                <div className="bg-amber-500/5 border border-amber-500/20 rounded-[2rem] p-6 mb-2">
                    <div className="flex items-center gap-2 mb-4">
                        <div className="w-8 h-8 rounded-xl bg-amber-500/20 flex items-center justify-center text-amber-600">
                            <Clock className="w-4 h-4" />
                        </div>
                        <h3 className="text-sm font-black uppercase tracking-widest text-amber-800">Pending Approvals</h3>
                    </div>
                    <div className="space-y-3">
                        {crmApprovals.filter(a => a.status === 'PENDING').map(a => (
                            <div key={a.id} className="flex flex-col md:flex-row md:items-center justify-between bg-card border border-border rounded-2xl p-4 shadow-sm gap-4 transition-all hover:shadow-md">
                                <div className="flex items-center gap-4">
                                    <StatusBadge variant="warning">PENDING</StatusBadge>
                                    <div className="flex flex-col">
                                        <span className="text-sm font-bold text-foreground">
                                            {a.actionType === 'BULK_CRM_UPDATE' ? 'Bulk Contact Update' : 'Data Export'}
                                        </span>
                                        <span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground opacity-60">
                                    {a.contactCount} Records · Requested {formatRelativeDistance(a.createdAt, {}, i18n.language)}
                                        </span>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Button onClick={() => handleApprovalAction(a.id, 'approve')} className="h-9 px-4 text-[10px] font-black uppercase tracking-widest bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl shadow-lg border-0 transition-all active:scale-95">
                                        Approve
                                    </Button>
                                    <Button onClick={() => handleApprovalAction(a.id, 'reject')} variant="outline" className="h-9 px-4 text-[10px] font-black uppercase tracking-widest border-rose-500/20 text-rose-600 hover:bg-rose-50 rounded-xl transition-all">
                                        Reject
                                    </Button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <DataTable
                columns={[
                    {
                        header: t('sidebar.contacts'),
                        accessor: 'lastName',
                        render: (c) => (
                            <div className="flex flex-col" onClick={(e) => { e.stopPropagation(); handleOpenDetail(c.id); }}>
                                <span className="text-sm font-bold text-foreground hover:text-primary transition-colors cursor-pointer">{c.displayName || `${c.firstName} ${c.lastName}`}</span>
                                <div className="flex items-center gap-1.5 mt-0.5 text-[10px] text-muted-foreground font-medium uppercase tracking-wider">
                                    <Mail className="w-3 h-3 opacity-50" />
                                    {c.canonicalEmail || c.email || t('contacts.noEmail')}
                                </div>
                            </div>
                        )
                    },
                    {
                        header: `${t('sidebar.companies')} & ${t('common.status')}`,
                        accessor: 'companyName',
                        render: (c) => (
                            <div className="flex flex-col gap-1.5 items-start">
                                {c.companyName || c.localCompanyId ? (
                                    <div className="flex items-center gap-1.5 text-xs font-bold text-foreground cursor-pointer hover:text-primary transition-colors" onClick={(e) => {
                                        e.stopPropagation();
                                        if (c.localCompanyId) {
                                            setSelectedCompanyId(c.localCompanyId);
                                            setIsCompanyDrawerOpen(true);
                                        } else if (c.companyName) {
                                            showSnackbar('Account only exists via global match. Edit contact to spawn local mapping.', 'info');
                                        }
                                    }}>
                                        <Building className="w-3.5 h-3.5 text-primary" />
                                        {c.companyName || 'Unknown'}
                                    </div>
                                ) : (
                                    <Badge 
                                        variant="outline" 
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleOpenDetail(c.id);
                                        }}
                                        className="text-[10px] text-muted-foreground border-dashed font-medium opacity-80 cursor-pointer hover:border-primary hover:text-primary transition-all"
                                    >
                                        <Plus className="w-2.5 h-2.5 mr-1 inline" /> Link Account
                                    </Badge>
                                )}
                                <div className="flex gap-2">
                                    {getStageBadge(c.stage)}
                                    {c.isStale && <StatusBadge variant="danger">STALE</StatusBadge>}
                                </div>
                            </div>
                        )
                    },
                    {
                        header: 'Verification',
                        accessor: 'verificationStatus',
                        render: (c) => (
                            <div className="flex flex-col gap-1.5 items-start">
                                <div className="flex items-center gap-2">
                                    {getVerificationIcon(c.verificationStatus || 'UNKNOWN')}
                                    <span className="text-[10px] font-black uppercase tracking-widest text-foreground">
                                        {c.verificationStatus || 'UNKNOWN'}
                                    </span>
                                </div>
                                <ScoreBadge score={c.confidenceScore || 0} />
                            </div>
                        )
                    },
                    {
                        header: 'Last Contact',
                        accessor: 'lastContactedAt',
                        render: (c) => (
                            <div className="flex flex-col">
                                <span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground opacity-60">
                                    {c.lastContactedAt ? t('contacts.lastContacted', { value: formatRelativeDistance(c.lastContactedAt, {}, i18n.language) }) : t('contacts.neverContacted')}
                                </span>
                                <span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground opacity-60 mt-0.5">
                                    {t('contacts.added', { value: formatRelativeDistance(c.createdAt || Date.now(), {}, i18n.language) })}
                                </span>
                            </div>
                        )
                    },
                    {
                        header: '',
                        accessor: 'id',
                        align: 'right',
                        render: (c) => (
                            <Button variant="ghost" size="sm" onClick={() => handleOpenDetail(c.id)} className="h-8 w-8 p-0 rounded-full hover:bg-primary/10 hover:text-primary transition-all">
                                <ChevronRight className="w-4 h-4" />
                            </Button>
                        )
                    }
                ]}
                data={contacts}
                loading={loading}
                pagination={{
                    page,
                    limit: 25,
                    totalRecords,
                    totalPages
                }}
                onPageChange={setPage}
                search={{
                    placeholder: t('contacts.searchPlaceholder'),
                    value: search,
                    onChange: (val) => { setSearch(val); }
                }}
                emptyState={
                    <div className="flex flex-col items-center justify-center p-12 text-center bg-card/50 rounded-xl border border-border/50 shadow-sm mx-4 my-8">
                        <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mb-6 shadow-glow border border-primary/20">
                            <Database className="w-8 h-8 text-primary opacity-80" />
                        </div>
                        <h3 className="text-xl font-black tracking-tight text-foreground mb-2">
                            {t('contacts.emptyTitle')}
                        </h3>
                        <p className="text-sm text-muted-foreground max-w-md mx-auto mb-8 leading-relaxed font-medium">
                            Your workspace CRM contains contacts you've saved or interacted with. 
                            You can discover new prospects matching <span className="text-foreground font-bold">"{search}"</span> from our shared Global Pool.
                        </p>
                        <div className="flex items-center gap-3">
                            {search && (
                                <Button variant="outline" onClick={() => { setSearch(''); setPage(1); fetchContacts(); }} className="h-10 px-4 rounded-xl font-bold text-xs uppercase tracking-widest">
                                    {t('actions.clear')}
                                </Button>
                            )}
                            <Button 
                                onClick={() => onSwitchTab('global')} 
                                className="h-10 px-6 rounded-xl font-bold bg-indigo-600 text-white text-xs uppercase tracking-widest shadow-lg shadow-indigo-600/20 hover:scale-105 transition-transform border-none hover:bg-indigo-700"
                            >
                                <Globe className="w-4 h-4 mr-2" /> Search Global Pool
                            </Button>
                        </div>
                    </div>
                }
                selectable={{
                    selectedIds: selectedContacts,
                    onSelectionChange: setSelectedContacts,
                    idAccessor: 'id'
                }}
                headerExtra={
                    <div className="flex flex-col gap-6">

                        {/* Saved Views Bar */}
                        {/* Saved Views Row */}
                        <div className="pt-2">
                            <SavedViewBar
                                savedViews={listContext.savedViews}
                                activeViewId={listContext.activeViewId}
                                viewsLoading={listContext.viewsLoading}
                                onApplyView={handleApplyView}
                                onSaveView={handleSaveView}
                                onDeleteView={listContext.deleteView}
                                onClearFilters={handleClearAllFilters}
                                hasActiveFilters={hasActiveFilters}
                            />
                        </div>

                        {/* Advanced Filters Matrix */}
                        {/* Advanced Filters Grid */}
                        <div className="flex flex-wrap items-center gap-3 pb-2 w-full">
                            <div className="space-y-1.5 min-w-[140px] flex-1">
                                <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground ml-1">{t('sidebar.companies')}</label>
                                <Select value={companyFilter} onValueChange={(val) => { setCompanyFilter(val); setPage(1); }}>
                                    <SelectTrigger className="w-full h-10 bg-background/50 border-2 border-border/50 text-[11px] font-bold rounded-xl shadow-sm">
                                        <SelectValue placeholder={t('contacts.allCompanies')} />
                                    </SelectTrigger>
                                    <SelectContent className="rounded-xl border-border shadow-xl max-h-[300px]">
                                        <SelectItem value="ALL" className="text-xs font-bold">{t('contacts.allCompanies')}</SelectItem>
                                        {workspaceCompanies.map(c => (
                                            <SelectItem key={c._id} value={c._id} className="text-xs font-medium">
                                                {c.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="space-y-1.5 min-w-[140px] flex-1">
                                <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground ml-1">Pipeline Stage</label>
                                <Select value={stageFilter} onValueChange={(val) => { setStageFilter(val); setPage(1); }}>
                                    <SelectTrigger className="w-full h-10 bg-background/50 border-2 border-border/50 text-[11px] font-bold rounded-xl shadow-sm">
                                        <SelectValue placeholder={t('contacts.allStages')} />
                                    </SelectTrigger>
                                    <SelectContent className="rounded-xl border-border shadow-xl">
                                        <SelectItem value="ALL" className="text-xs font-bold">{t('contacts.allStages')}</SelectItem>
                                        <SelectItem value="LEAD" className="text-xs font-bold">Lead</SelectItem>
                                        <SelectItem value="CONTACTED" className="text-xs font-bold">Contacted</SelectItem>
                                        <SelectItem value="REPLIED" className="text-xs font-bold">Replied</SelectItem>
                                        <SelectItem value="MEETING_BOOKED" className="text-xs font-bold">Meeting Booked</SelectItem>
                                        <SelectItem value="CLOSED_WON" className="text-xs font-bold">Won</SelectItem>
                                        <SelectItem value="CLOSED_LOST" className="text-xs font-bold">Lost</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="space-y-1.5 min-w-[140px] flex-1">
                                <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground ml-1">Verification</label>
                                <Select value={verificationFilter} onValueChange={(val) => { setVerificationFilter(val); setPage(1); }}>
                                    <SelectTrigger className="w-full h-10 bg-background/50 border-2 border-border/50 text-[11px] font-bold rounded-xl shadow-sm">
                                        <SelectValue placeholder="Status" />
                                    </SelectTrigger>
                                    <SelectContent className="rounded-xl border-border shadow-xl">
                                        <SelectItem value="ALL" className="text-xs font-bold">All Status</SelectItem>
                                        <SelectItem value="VERIFIED" className="text-xs font-bold">Verified</SelectItem>
                                        <SelectItem value="RISKY" className="text-xs font-bold">Risky</SelectItem>
                                        <SelectItem value="INVALID" className="text-xs font-bold">Invalid</SelectItem>
                                        <SelectItem value="UNKNOWN" className="text-xs font-bold">Unknown</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="space-y-1.5 min-w-[140px] flex-1">
                                <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground ml-1">Contacted</label>
                                <Select value={contactedFilter} onValueChange={(val) => { setContactedFilter(val); setPage(1); }}>
                                    <SelectTrigger className="w-full h-10 bg-background/50 border-2 border-border/50 text-[11px] font-bold rounded-xl shadow-sm">
                                        <SelectValue placeholder="Contacted" />
                                    </SelectTrigger>
                                    <SelectContent className="rounded-xl border-border shadow-xl">
                                        <SelectItem value="ALL" className="text-xs font-bold">Any status</SelectItem>
                                        <SelectItem value="true" className="text-xs font-bold">Contacted</SelectItem>
                                        <SelectItem value="false" className="text-xs font-bold">Not yet</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="space-y-1.5 min-w-[140px] flex-1">
                                <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground ml-1">Data Recency</label>
                                <Select value={staleFilter} onValueChange={(val) => { setStaleFilter(val); setPage(1); }}>
                                    <SelectTrigger className="w-full h-10 bg-background/50 border-2 border-border/50 text-[11px] font-bold rounded-xl shadow-sm">
                                        <SelectValue placeholder="Recency" />
                                    </SelectTrigger>
                                    <SelectContent className="rounded-xl border-border shadow-xl">
                                        <SelectItem value="ALL" className="text-xs font-bold">All</SelectItem>
                                        <SelectItem value="false" className="text-xs font-bold">Fresh Data Only</SelectItem>
                                        <SelectItem value="true" className="text-xs font-bold">Stale (Outdated)</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="space-y-1.5 min-w-[140px] flex-1">
                                <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground ml-1">Overrides</label>
                                <Select value={hasOverrideFilter} onValueChange={(val) => { setHasOverrideFilter(val); setPage(1); }}>
                                    <SelectTrigger className="w-full h-10 bg-background/50 border-2 border-border/50 text-[11px] font-bold rounded-xl shadow-sm">
                                        <SelectValue placeholder="Overrides" />
                                    </SelectTrigger>
                                    <SelectContent className="rounded-xl border-border shadow-xl">
                                        <SelectItem value="ALL" className="text-xs font-bold">All records</SelectItem>
                                        <SelectItem value="true" className="text-xs font-bold">Has Override Email</SelectItem>
                                        <SelectItem value="false" className="text-xs font-bold">Original Data Only</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="space-y-1.5 min-w-[140px] flex-1">
                                <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground ml-1">Tags</label>
                                <div className="relative group">
                                    <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground group-focus-within:text-primary transition-colors" />
                                    <input
                                        placeholder="Filtered tags..."
                                        className="w-full h-10 pl-9 pr-3 bg-background/50 border-2 border-border/50 text-[11px] font-bold rounded-xl shadow-sm focus:border-primary transition-all outline-none"
                                        value={tagsFilter}
                                        onChange={(e) => { setTagsFilter(e.target.value); setPage(1); }}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                }
                bulkActions={[
                    {
                        label: "Campaign",
                        icon: Plus,
                        onClick: handleOpenCampaignModal,
                        variant: "indigo"
                    },
                    {
                        label: "Stage",
                        icon: Layers,
                        onClick: () => setIsBulkStageOpen(true)
                    },
                    {
                        label: "Tag",
                        icon: Tag,
                        onClick: () => setIsBulkTagOpen(true)
                    },
                    {
                        label: "Export",
                        icon: Download,
                        onClick: () => handleExport("selected")
                    }
                ]}
            />

            {isEnrollModalOpen && (
                <SequenceEnrollModal
                    personIds={selectedContacts}
                    onClose={() => setIsEnrollModalOpen(false)}
                    onComplete={() => {
                        setIsEnrollModalOpen(false);
                        setSelectedContacts([]);
                        fetchContacts();
                        showSnackbar(`${selectedContacts.length} contact enrollment process completed`, 'success');
                    }}
                />
            )}

            <AddToCampaignModal
                isOpen={isAddCampaignOpen}
                onClose={() => setIsAddCampaignOpen(false)}
                workspaceContactIds={selectedContacts}
                onSuccess={() => {
                    setSelectedContacts([]);
                    fetchContacts();
                    showSnackbar(`${selectedContacts.length} contacts added to campaign`, 'success');
                }}
            />

            {/* Contact Detail Drawer */}
            <ContactDetailDrawer
                isOpen={isDetailModalOpen}
                onClose={() => setIsDetailModalOpen(false)}
                contactId={selectedContactIdForDetail}
                onSaveSuccess={fetchContacts}
            />

            {/* Bulk Stage Update Modal */}
            <Sheet open={isBulkStageOpen} onOpenChange={setIsBulkStageOpen}>
                <SheetContent side="right">
                    <SheetHeader>
                        <SheetTitle>Toplu Aşama Güncelle</SheetTitle>
                        <SheetDescription>
                            Seçilen {selectedContacts.length} kişinin CRM aşamasını değiştirin.
                        </SheetDescription>
                    </SheetHeader>
                    <div className="py-4">
                        <Select value={bulkStageValue} onValueChange={setBulkStageValue}>
                            <SelectTrigger>
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="LEAD">Lead</SelectItem>
                                <SelectItem value="CONTACTED">Contacted</SelectItem>
                                <SelectItem value="REPLIED">Replied</SelectItem>
                                <SelectItem value="MEETING_BOOKED">Meeting Booked</SelectItem>
                                <SelectItem value="CLOSED_WON">Won</SelectItem>
                                <SelectItem value="CLOSED_LOST">Lost</SelectItem>
                                <SelectItem value="UNQUALIFIED">Unqualified</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <SheetFooter>
                        <Button variant="outline" onClick={() => setIsBulkStageOpen(false)}>İptal</Button>
                        <Button onClick={handleBulkStageUpdate} disabled={isBulkProcessing}>
                            {isBulkProcessing ? 'Güncelleniyor...' : 'Güncelle'}
                        </Button>
                    </SheetFooter>
                </SheetContent>
            </Sheet>

            {/* Bulk Tag Modal */}
            <Sheet open={isBulkTagOpen} onOpenChange={setIsBulkTagOpen}>
                <SheetContent side="right">
                    <SheetHeader>
                        <SheetTitle>Toplu Etiket İşlemi</SheetTitle>
                        <SheetDescription>
                            Seçilen {selectedContacts.length} kişi üzerinde etiket işlemi uygulayın.
                        </SheetDescription>
                    </SheetHeader>
                    <div className="py-4 space-y-3">
                        <Select value={bulkTagMode} onValueChange={setBulkTagMode}>
                            <SelectTrigger>
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="add">Ekle (Mevcut etiketlere ekle)</SelectItem>
                                <SelectItem value="remove">Kaldır (Bu etiketleri çıkar)</SelectItem>
                                <SelectItem value="replace">Değiştir (Tümünü bu etiketlerle değiştir)</SelectItem>
                            </SelectContent>
                        </Select>
                        <Input
                            value={bulkTagValue}
                            onChange={(e) => setBulkTagValue(e.target.value)}
                            placeholder="tag1, tag2, tag3"
                        />
                    </div>
                    <SheetFooter>
                        <Button variant="outline" onClick={() => setIsBulkTagOpen(false)}>İptal</Button>
                        <Button onClick={handleBulkTagAdd} disabled={isBulkProcessing || !bulkTagValue.trim()}>
                            {isBulkProcessing ? 'İşleniyor...' : bulkTagMode === 'add' ? 'Ekle' : bulkTagMode === 'remove' ? 'Kaldır' : 'Değiştir'}
                        </Button>
                    </SheetFooter>
                </SheetContent>
            </Sheet>

            {/* Workspace Tag Management Modal */}
            <Sheet open={isTagManagerOpen} onOpenChange={setIsTagManagerOpen}>
                <SheetContent side="right" className="sm:max-w-[500px]">
                    <SheetHeader>
                        <SheetTitle>Çalışma Alanı Etiket Yönetimi</SheetTitle>
                        <SheetDescription>
                            Workspace genelindeki tüm etiketleri görüntüleyin, isimlerini değiştirin veya silin.
                        </SheetDescription>
                    </SheetHeader>
                    <div className="max-h-[60vh] overflow-y-auto mt-4 px-1">
                        {workspaceTags.length === 0 ? (
                            <p className="text-sm text-muted-foreground text-center py-4">Kullanımda etiket bulunmuyor.</p>
                        ) : (
                            <div className="space-y-2">
                                {workspaceTags.map(wt => (
                                    <div key={wt.tag} className="flex items-center justify-between p-2 border rounded hover:bg-muted/30">
                                        <div className="flex items-center gap-2">
                                            <Badge variant="outline">{wt.tag}</Badge>
                                            <span className="text-xs text-muted-foreground">({wt.count} kişi)</span>
                                        </div>
                                        <div className="flex items-center gap-1">
                                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 hover:bg-accent hover:text-accent-foreground" onClick={() => handleRenameTag(wt.tag)} title="Yeniden Adlandır">
                                                <Pencil className="h-3 w-3" />
                                            </Button>
                                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-destructive hover:text-destructive hover:bg-destructive/10" onClick={() => handleDeleteTag(wt.tag)} title="Kaldır">
                                                <X className="h-3 w-3" />
                                            </Button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </SheetContent>
            </Sheet>
            {/* Workspace Local CRM Navigational Drawer */}
            <CompanyDetailDrawer 
                isOpen={isCompanyDrawerOpen} 
                onClose={() => setIsCompanyDrawerOpen(false)} 
                companyId={selectedCompanyId} 
                onSaveSuccess={() => fetchContacts()}
                onContactClick={(id) => {
                    setIsCompanyDrawerOpen(false);
                    handleOpenDetail(id);
                }}
            />
            <ContactCreateModal
                isOpen={isCreateModalOpen}
                onClose={() => setIsCreateModalOpen(false)}
                onSuccess={() => {
                    fetchContacts();
                    fetchFunnel();
                }}
            />
            <ContactImportIntakeModal
                isOpen={isImportModalOpen}
                onClose={() => setIsImportModalOpen(false)}
            />
        </div>
    );
};

export default Contacts;
